import { yupResolver } from "@hookform/resolvers/yup";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import * as yup from "yup";
import { AxiosResponse } from "axios";
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import { selectorTransversal } from "../../../../dtos/selector";
import { EntidadRecaudadora, StatusCode, TipoFuncionalidad } from "../../../../utils/enums";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import CustomAccordion from "../../../../components/ui/materialui/customAccordion";
import InputFecha from "../../../../components/inputs/yup/materialui/inputFecha";
import InputSelect from "../../../../components/inputs/yup/materialui/inputSelect";
import MButton from "../../../../components/inputs/yup/materialui/mButton";
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import InputFileUpload from "../../../../components/inputs/yup/materialui/inputFileUpload";
import { exportExcel } from "../../../../utils/exportExcel";
import TableHeader from "../../../../components/ui/materialui/tableHeader";
import { useColumns } from "../utils/columns";
import { ArgumentosRecaudacionDTO, DetalleArchivoRecaudadoDto, RespuestaDividendoProcesarDTO } from "../models/registrarRecaudacionMasiva";
import { useRegistrarRecaudacionMasiva } from "../hooks/useRegistrarRecaudacionMasiva";
import { useColumnsError } from "../utils/columnsError";
import Breadcrumb from "../../../../components/ui/materialui/breadcrumb";
import TablaSelector from "../../../../components/ui/materialui/tablaSelector";
import Tabla from "../../../../components/ui/materialui/tabla";
import TabsContainer from "../../../../components/ui/materialui/TabsContainer";
import RecaudacionMasiva from "../components/recaudacionMasiva";
import ConfirmationModal from "../../../../components/ui/materialui/confirmationModal";
import { useToast } from "../../../../hooks/useToast";
import { getRecaudadores } from "../../../dividendo/services/servicesRecaudacionIndividual";
import { obtenerEstadoRol } from "../../../remesas/administracionRemesas/services/serviceBloqueoRecaudacion";
import { RolResponse } from "../../../remesas/administracionRemesas/models/RolResponse";
import { ListResponseDto } from "../../../../dtos/ListResponseDto";
import { Recaudador } from "../../../dividendo/models/recaudador";
import { PermissionGuard } from "../../../../components/auth/ProtectedRoute";
import { ProgresoRecaudacionResponse } from "../services/recaudacionMasivaApiService";
import { getCurrentUsername } from '../../../../services/userRolesService';

export interface RegistrarRecaudacionMasiva {
    entidadRecaudadora: string;
    fechaRecaudacion: Date;
    archivo: File;
}

const ENTIDADES_RECAUDADORAS_DESHABILITADAS = [
    
    { rut: "33333333-3", nombre: "PLAN WEB SERVIPAG" },
    { rut: "44444444-4", nombre: "PAGO WEB BANCO SANTANDER" },
    { rut: "55555555-5", nombre: "PAGO WEB TBK" },
    { rut: "66666666-6", nombre: "ONE CLICK" },
    { rut: "76001637-3", nombre: "PAGO WEB BCI" },
    { rut: "76028396-7", nombre: "COBRATEL SPA" },
    { rut: "76040181-1", nombre: "FASTCO" },
    { rut: "88888888-8", nombre: "SERVICIO AL CLIENTE" },
    { rut: "96708800-5", nombre: "SERVICNCO S.A." },
    { rut: "97004000-5", nombre: "BANCO DE CHILE" },
    { rut: "98765432-5", nombre: "POSTERGACION DE DIVIDENDOS" },
    { rut: "99147000-K", nombre: "CESANTIA" },
    { rut: "99512160-3", nombre: "Metlife Cia Seguros" },
    { rut: "99902503-K", nombre: "EMPRESA 2" },
    { rut: "99963345-5", nombre: "EMPRESA 3" },
    { rut: "78908450-5", nombre: "ECOVAL" },
    { rut: "99514870-6", nombre: "Caja MetLife" },
    { rut: "22222222-2", nombre: "Plan de Pago" } 
    
] as const;

const schema = yup.object().shape({
    entidadRecaudadora: yup.string().required("Entidad recaudadora es requerida"),
    fechaRecaudacion: yup.date().typeError("Fecha recaudación es requerida").required("Fecha recaudación es requerida"),
    archivo: yup
        .mixed<File>()
        .required("Archivo es requerido")
        .test("fileExists", "Archivo es requerido", value => value instanceof File)
});

function RegistrarRecaudacion() {
    const entidadesRecaudadorasEnListaNegra = useMemo(
        () => new Set<string>(ENTIDADES_RECAUDADORAS_DESHABILITADAS.map(entidad => entidad.rut)),
        []
    );

    const {
        formState: { errors },
        control,
        handleSubmit,
        setValue,
        watch
    } = useForm<RegistrarRecaudacionMasiva>({
        mode: "onBlur",
        resolver: yupResolver(schema) as unknown as Resolver<RegistrarRecaudacionMasiva>
    });

    const dataForm = watch();

    const {
        data,
        dataError,
        recaudacionId,
        recaudacionEnProceso,
        recaudacionCompletada,
        progresoActual,
        isConnected,
        getRegistrarRecaudacionMasiva,
        getDetalleArchivoRecaudado,
        iniciarRecaudacionMasiva,
        cancelarRecaudacion,
        limpiarRecaudacion,
        actualizarResultadosRecaudacion,
        refrescarDatos
    } = useRegistrarRecaudacionMasiva();
    const [mostrarTablas, setMostrarTablas] = useState(false);
    const [filasSeleccionada, setFilasSeleccionadas] = useState<DetalleArchivoRecaudadoDto[]>([]);
    const [dataCargadaPorPrimeraVez, setDataCargadaPorPrimeraVez] = useState(false);
    const { addToast } = useToast();

    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: "",
        message: "",
        metodo: () => { },
    });

    const [activeTab, setActiveTab] = useState(0);
    const [accordionExpanded, setAccordionExpanded] = useState(true);
    const ultimoRecaudacionRefrescadaRef = useRef<string | null>(null);

    const { COLUMNS } = useColumns();
    const columns = useMemo(() => COLUMNS, [data]);

    const { COLUMNS: COLUMNSERROR } = useColumnsError();
    const columnsError = useMemo(() => COLUMNSERROR, [dataError]);

    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'Recaudación masiva', url: `/recaudacionMasiva` },
        { label: 'Registrar recaudación', active: true },
    ];

    const [inputKey, setInputKey] = useState(Date.now()); //KEY PARA INPUTFILE
    const [recaudador, setRecaudador] = useState<selectorTransversal[]>([]);

    // Monitorear cambios en recaudacionId para debugging
    useEffect(() => {
        console.log('🔄 recaudacionId cambió:', recaudacionId);
        if (recaudacionId) {
            console.log('📋 Nuevo recaudacionId asignado:', recaudacionId);
        }
    }, [recaudacionId]);


    const tipoEntidad = async () => {
        const resp: AxiosResponse<ListResponseDto<Recaudador>> = await getRecaudadores();
        if (resp.status === StatusCode.Ok) {
            const data: Recaudador[] = await resp.data.items;
            const options = data
            .filter((m: Recaudador) => !entidadesRecaudadorasEnListaNegra.has(m.rut))
            .map((m: Recaudador) => ({
                label: m.nombre,
                value: m.rut,
            })); setRecaudador(options);
        }
        return;
    }

    useEffect(() => {
        const inicializar = async () => {
            await tipoEntidad();
            setValue("fechaRecaudacion", new Date());
        };

        inicializar();
    }, [setValue]);

    const handleEntidadChange = async (selectedValue: string | null) => {
        if (selectedValue) {
            const resp = await getDetalleArchivoRecaudado(selectedValue);
            setMostrarTablas(true);
            setFilasSeleccionadas([]);
        } else {
            setMostrarTablas(false);
            setFilasSeleccionadas([]);
        }

    };

    const onSubmit = async (data: RegistrarRecaudacionMasiva) => {
        const fechaRecaudacion =
            typeof data.fechaRecaudacion === "string"
                ? new Date(data.fechaRecaudacion)
                : data.fechaRecaudacion;

        const dataRequest: ArgumentosRecaudacionDTO = {
            rutRecaudador: data.entidadRecaudadora,
            fechaRecaudacion,
            archivo: data.archivo,
            nombreArchivo: dataForm.archivo.name,
            Login: getCurrentUsername()
        };

        const resp: RespuestaDividendoProcesarDTO = await getRegistrarRecaudacionMasiva(dataRequest);

        if (resp?.dividendosProcesar && resp.dividendosProcesar.length > 0 &&  resp.erroresCarga && resp.erroresCarga.length === 0) {
            addToast({
                mostrar: true,
                titulo: "Éxito",
                tipo: "Success",
                mensaje: "Archivo cargado correctamente"
            });

        } else if (resp?.erroresCarga && resp.erroresCarga.length > 0 && resp.dividendosProcesar && resp.dividendosProcesar.length === 0) {
            addToast({
                mostrar: true,
                titulo: "Advertencia",
                tipo: "Warning",
                mensaje: "El archivo contiene errores de carga"
            });
        }

        else if (resp?.dividendosProcesar && resp.dividendosProcesar.length > 0 && resp.erroresCarga && resp.erroresCarga.length > 0) {
            addToast({
                mostrar: true,
                titulo: "Advertencia",
                tipo: "Warning",
                mensaje: "Archivo cargado correctamente, pero contiene errores de carga"
            });
        }


        await getDetalleArchivoRecaudado(data.entidadRecaudadora);

        setMostrarTablas(true);
        setFilasSeleccionadas([]);
    };

    const isFechaEditable = useMemo(() => {
        const opcionesEditables = [EntidadRecaudadora.PATREC, EntidadRecaudadora.PACBCI];
        return opcionesEditables.includes(dataForm.entidadRecaudadora as EntidadRecaudadora);
    }, [dataForm.entidadRecaudadora]);

    const handleRecaudar = async () => {
        if (filasSeleccionada.length === 0) {
            addToast({
                mostrar: true,
                titulo: "Advertencia",
                tipo: "Warning",
                mensaje: "Debe seleccionar al menos una fila antes de proceder con la recaudación."
            });
            return;
        }

        const response = await obtenerEstadoRol("ADM");
        const rol = response.data as RolResponse;
        const isBloqueado = rol.bloqueado === "N" ? false : true;

        const confirmAction = async () => {
            try {
                // Iniciar el proceso de recaudación
                const recaudacionResponse = await iniciarRecaudacionMasiva(filasSeleccionada);

                if (recaudacionResponse?.recaudacionId) {

                    // Limpiar y cambiar de vista solo cuando el backend confirma inicio exitoso
                    setActiveTab(2);
                    setAccordionExpanded(false);

                    addToast({
                        mostrar: true,
                        titulo: "Proceso Iniciado",
                        tipo: "Success",
                        mensaje: `Recaudación iniciada exitosamente. ID: ${recaudacionResponse.recaudacionId}`
                    });

                }
            } catch (error) {
                const isAxiosError = !!(error as any)?.isAxiosError;
                if (!isAxiosError) {
                    addToast({
                        mostrar: true,
                        titulo: "Error",
                        tipo: "Danger",
                        mensaje: error instanceof Error ? error.message : "Error al procesar la recaudación. Intente nuevamente."
                    });
                }

                // Expandir nuevamente el accordion del formulario en caso de error
                setAccordionExpanded(true);
            }
        };

        if (!isBloqueado) {

            setModalConfirmacion({
                show: true,
                title: "Confirmar recaudación",
                message: `¿Está seguro de que desea proceder con la recaudación de ${filasSeleccionada.length} fila(s) seleccionada(s)?`,
                metodo: confirmAction,
            });

        } else {
            addToast({
                mostrar: true,
                titulo: "Recaudación Bloqueada",
                tipo: "Danger",
                mensaje: "La recaudación se encuentra bloqueada actualmente. Por ahora no es posible proceder."
            });
        }
    };

    const handleProgresoCompletado = useCallback(async (resultados: ProgresoRecaudacionResponse) => {
        actualizarResultadosRecaudacion(resultados);

        const recaudacionFinalizadaId = resultados?.recaudacionId ?? resultados?.recaudacionId ?? null;
        if (recaudacionFinalizadaId && ultimoRecaudacionRefrescadaRef.current === recaudacionFinalizadaId) {
            return;
        }

        const entidadSeleccionada = dataForm.entidadRecaudadora;
        if (entidadSeleccionada) {
            await getDetalleArchivoRecaudado(entidadSeleccionada);
            setFilasSeleccionadas([]);
            ultimoRecaudacionRefrescadaRef.current = recaudacionFinalizadaId;
        }
    }, [actualizarResultadosRecaudacion, dataForm.entidadRecaudadora, getDetalleArchivoRecaudado]);

    const tabDatosProcesados = (
        <TablaSelector<DetalleArchivoRecaudadoDto>
            columns={columns}
            data={data}
            esMultiple={true}
            setFilaSeleccionada={filasSeleccionada => setFilasSeleccionadas(filasSeleccionada)}
            filasSeleccionadas={filasSeleccionada}
            titulo={
                <TableHeader
                    title=""
                    buttonExport={
                        <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                            <MButton
                                type="button"
                                variant="outlined"
                                onClick={() => exportExcel(data, columns, "Recaudación masiva")}
                                startIcon={<SummarizeTwoToneIcon />}
                            >
                                Exportar
                            </MButton>
                        </PermissionGuard>
                    }
                    button={
                        <MButton
                            type="button"
                            variant="contained"
                            onClick={handleRecaudar}
                            disabled={filasSeleccionada.length === 0}
                        >
                            {`Recaudar (${filasSeleccionada.length})`}
                        </MButton>
                    }
                />
            }
        />
    );

    const tabErrores = (
        <Tabla
            columns={columnsError}
            data={dataError}
        />
    );

    const tabs = [
        {
            label: `Cargados`,
            content: tabDatosProcesados
        },
        {
            label: `Errores carga (${dataError?.length || 0})`,
            content: tabErrores
        },
        {
            label: 'Resultados recaudación',
            content: <RecaudacionMasiva
                onProgresoCompletado={handleProgresoCompletado}
            />
        }
    ];

    return (
        <Suspense>
            <Breadcrumb items={breadcrumbItems} />
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Suspense>
                        <CustomAccordion
                            title="Registrar recaudación"
                            expandedDefault
                            expanded={accordionExpanded}
                            onExpandedChange={setAccordionExpanded}
                        >
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Grid2 container spacing={2}>
                                    <Grid2 size={3}>
                                        <InputSelect
                                            name="entidadRecaudadora"
                                            control={control}
                                            label="Entidad recaudadora"
                                            options={recaudador}
                                            error={errors.entidadRecaudadora}
                                            onChange={handleEntidadChange}
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <InputFecha
                                            name="fechaRecaudacion"
                                            control={control}
                                            label="Fecha"
                                            error={errors.fechaRecaudacion}
                                            esReadOnly={!isFechaEditable}
                                        />
                                    </Grid2>

                                    <Grid2 container spacing={1} alignItems="flex-end" sx={{ mt: 2, justifyContent: "flex-start" }}>
                                        <Grid2 size="auto">
                                            <InputFileUpload
                                                name="archivo"
                                                control={control}
                                                label=""
                                                accept=".txt"
                                                fileKey={inputKey}
                                            />
                                        </Grid2>
                                        <Grid2 size="auto">
                                            <MButton
                                                type="button"
                                                variant="contained"
                                                startIcon={<DeleteIcon />}
                                                sx={{ ml: 1 }}
                                                onClick={() => {
                                                    const input = document.querySelector<HTMLInputElement>('input[type="file"][name="archivo"]');
                                                    if (input) input.value = '';
                                                    setValue("archivo", {} as File);
                                                    setInputKey(Date.now());
                                                }}
                                            >
                                                Limpiar
                                            </MButton>
                                        </Grid2>
                                        <Grid2 size="auto">
                                            <MButton
                                                type="submit"
                                                variant="contained"
                                                startIcon={<ArrowCircleUpIcon />}
                                                sx={{ ml: 1 }}
                                            >
                                                Cargar
                                            </MButton>
                                        </Grid2>
                                    </Grid2>
                                </Grid2>
                            </form>
                        </CustomAccordion>

                        {mostrarTablas && (
                            <Box sx={{ mt: 3 }}>
                                <TabsContainer
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    onTabChange={setActiveTab}
                                />
                            </Box>
                        )}
                    </Suspense>
                </Paper>
            </Box>

            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({ ...modalConfirmacion, show: false })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                message={modalConfirmacion.message}
                title={modalConfirmacion.title}
            />
        </Suspense>
    );
}

export default RegistrarRecaudacion;